import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of, from } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  pluck,
  share,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AuthAccessor } from 'src/app/services/accessors/auth.accessor';
import { FirebaseAuthService } from 'src/app/services/firebase-auth.service';

import { AuthService } from 'src/app/services/api/auth.service';
import { QueryClient } from 'src/app/core/query';

import * as AuthActions from '../actions/auth/auth.actions';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authApi: AuthService,
    private firebaseAuth: FirebaseAuthService,
    private authAccessor: AuthAccessor,
    private queryClient: QueryClient,
  ) {}

  setToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setToken),
        tap(({ token }) => {
          this.authAccessor.remember$.pipe(take(1)).subscribe((remember) => {
            if (remember) {
              if (token) {
                localStorage.setItem('admin-token', token);
              } else localStorage.removeItem('admin-token');
            }
          });
        }),
      ),
    { dispatch: false },
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap((action) =>
        from(this.firebaseAuth.signIn(action.email, action.password)).pipe(
          map((token) =>
            AuthActions.loginSuccess({
              token,
              remember: action.remember,
            }),
          ),
          catchError((e) => of(AuthActions.loginFailed({ error: { message: this.getFirebaseErrorMessage(e) } }))),
        ),
      ),
      share(),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      map(({ token }) =>
        AuthActions.setToken({ token }),
      ),
      share(),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(ofType(AuthActions.logout)),
  ).pipe(share());

  // Cached queries belong to the signed-in admin, so the next user must start with an empty cache.
  clearQueryCache$ = createEffect(
    () =>
      this.actions$.pipe(
        // logoutSuccess too: FlowService ejects a non-admin with logoutSuccess alone, without logout.
        ofType(AuthActions.logout, AuthActions.logoutSuccess, AuthActions.loginSuccess),
        tap(() => this.queryClient.clear()),
      ),
    { dispatch: false },
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      exhaustMap(() =>
        from(this.firebaseAuth.getIdToken(true)).pipe(
          switchMap((token) => {
            if (token) {
              localStorage.setItem('admin-token', token);
              return [
                AuthActions.setToken({ token }),
                AuthActions.refreshTokenSuccess(),
              ];
            }
            return [AuthActions.refreshTokenFailed({ error: { message: 'No token available' } })];
          }),
          catchError((e) => of(AuthActions.refreshTokenFailed({ error: e }))),
        ),
      ),
      share(),
    ),
  );

  refreshTokenFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenFailed),
      map(() => AuthActions.logout()),
    ),
  );
  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      pluck('email'),
      exhaustMap((email) =>
        this.authApi.forgotPassword(email).pipe(
          map(() => AuthActions.forgotPasswordSuccess()),
          catchError((e) => of(AuthActions.forgotPasswordFailed({ error: e }))),
        ),
      ),
      share(),
    ),
  );
  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      exhaustMap(({ _id, password, token }) =>
        this.authApi.resetPassword(_id, password, token).pipe(
          map(() => AuthActions.resetPasswordSuccess()),
          catchError((error) => of(AuthActions.resetPasswordFailed({ error }))),
        ),
      ),
      share(),
    ),
  );

  private getFirebaseErrorMessage(error: any): string {
    const code = error?.code;
    switch (code) {
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'Invalid email or password.';
      case 'auth/wrong-password':
        return 'Invalid email or password.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/invalid-tenant-id':
        return 'Authentication configuration error. Please contact support.';
      case 'auth/password-does-not-meet-requirements':
        return 'Your current password doesn’t meet our security requirements. Please reset your password to continue.';
      case 'auth/internal-error':
        return this.extractBlockingFnMessage(error?.message) || 'Login failed. Please try again.';
      default:
        return this.extractBlockingFnMessage(error?.message) || error?.message || 'Login failed. Please try again.';
    }
  }

  private extractBlockingFnMessage(raw: any): string | null {
    if (typeof raw !== 'string') return null;
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      const parsed = JSON.parse(match[0]);
      const err = parsed?.error ?? parsed;
      const msg = err?.message;
      if (typeof msg !== 'string' || !msg.trim()) return null;
      const retryAfterSeconds = Number(err?.retryAfterSeconds);
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        const base = msg
          .trim()
          .replace(/\s*Please try again later\.?\s*$/i, '')
          .replace(/\.?\s*$/, '');
        return `${base}. Try again in ${this.formatDuration(retryAfterSeconds)}.`;
      }
      return msg.trim();
    } catch {
      return null;
    }
  }

  private formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${seconds} second${seconds === 1 ? '' : 's'}`;
    }
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
}
